// 스킬 슬롯 6칸 × 2페이지(D1e, docs/SKILL-TREE.md 11.3): 순수 규칙과 메모리 상태. 저장은 game/skillSlots.ts가 얹는다. three import 금지.
// 슬롯은 입력층·HUD의 배치일 뿐이다. 시뮬레이션은 지금처럼 Btn.Skill1..5만 보므로 쿨·MP·캔슬·연계·결정성은 달라지지 않는다.
import { SKILL_BTN, SKILL_BTN_ALL, SKILL_MAX } from './types';

export const SLOTS_PER_PAGE = 6;
export const SLOT_PAGES = 2;
export const SLOT_COUNT = SLOTS_PER_PAGE * SLOT_PAGES;
/**
 * 환생 추가 칸(레인 R, REBIRTH-MODES-SPEC 2.2 "스킬 슬롯 +1, 6 → 7칸"): 페이지마다 7번째 칸 하나. 칸 번호 SLOT_COUNT + 페이지(12·13).
 * 기본 12칸 저장(skillslots.v1 slots 길이 12)은 그대로이고 추가 칸은 선택 필드 x7로 따로 싣는다(옛 빌드는 x7을 모른 채 12칸만 읽는다)
 */
export const EXTRA_SLOT_BASE = SLOT_COUNT;
export const SLOT_TOTAL = SLOT_COUNT + SLOT_PAGES;
export const isExtraSlot = (slot: number): boolean => slot >= EXTRA_SLOT_BASE && slot < SLOT_TOTAL;
/** 슬롯에 둘 수 있는 스킬 번호 1..SKILL_COUNT(0 = 빈 칸). 카엘 기본 = Btn.Skill1..5. 스킬이 더 많은 영웅(라이라 11)은 count 인자로 넓힌다(c022) */
export const SKILL_COUNT = SKILL_BTN.length;

export type SlotDenial = 'bad_slot' | 'bad_skill' | 'not_learned' | 'not_village';
export type SlotCode = 'ok' | SlotDenial | 'noop';

/** HUD·입력이 읽는 뷰(GameView.slots). slots는 같은 배열을 재사용한다 */
export interface SlotsView {
  /** 현재 페이지 0..SLOT_PAGES-1 */
  readonly page: number;
  /** 슬롯 12개의 스킬 번호(0 = 빈 칸). 페이지 p의 칸 n = slots[p×6 + n] */
  readonly slots: ArrayLike<number>;
  /** 바뀔 때마다 1 증가(UI 재그리기 키) */
  readonly revision: number;
  /** 페이지마다 더 열린 칸 수(환생 1차부터 1 = 7번째 칸). 없으면 0 */
  readonly extra?: number;
  /** 기본 12칸 + 추가 칸 2(SLOT_TOTAL). slots는 이 배열의 앞 12칸 보기다. 없으면 slots만(옛 픽스처) */
  readonly all?: ArrayLike<number>;
}
/** 추가 칸까지 든 배치(없으면 기본 12칸) */
export const slotsAll = (sv: SlotsView): ArrayLike<number> => sv.all ?? sv.slots;

/** 슬롯 번호 → 입력 비트(빈 칸·범위 밖 = 0) */
export function slotBtn(slots: ArrayLike<number>, slot: number): number {
  if (!(slot >= 0 && slot < slots.length && slot < SLOT_TOTAL)) return 0;
  const skill = slots[slot] | 0;
  return skill >= 1 && skill <= SKILL_MAX ? SKILL_BTN_ALL[skill - 1] : 0;
}
/** 키 n(0..5)가 가리키는 슬롯: 현재 페이지(other = false) 또는 다른 페이지(other = true) */
export function keySlot(page: number, n: number, other: boolean): number {
  const p = other ? (page + 1) % SLOT_PAGES : page;
  // 키 7(n = 6)은 그 페이지의 환생 추가 칸
  return n === SLOTS_PER_PAGE ? EXTRA_SLOT_BASE + p : p * SLOTS_PER_PAGE + n;
}
/** 스킬 번호가 놓인 슬롯, 없으면 -1 */
export function slotOfSkill(slots: ArrayLike<number>, skill: number): number {
  const n = Math.min(slots.length, SLOT_TOTAL);
  for (let i = 0; i < n; i++) if (slots[i] === skill) return i;
  return -1;
}
/** 칸 번호 → 페이지(추가 칸 12·13은 0·1쪽) */
export const pageOfSlot = (slot: number): number => (isExtraSlot(slot) ? slot - EXTRA_SLOT_BASE : Math.floor(slot / SLOTS_PER_PAGE));
/** 칸 번호 → 페이지 안 칸(0..5, 추가 칸은 6) */
export const cellOfSlot = (slot: number): number => (isExtraSlot(slot) ? SLOTS_PER_PAGE : slot % SLOTS_PER_PAGE);
/** 페이지 page의 cell(0..6)번 칸 번호 */
export const slotAt = (page: number, cell: number): number => (cell >= SLOTS_PER_PAGE ? EXTRA_SLOT_BASE + page : page * SLOTS_PER_PAGE + cell);
/** 배치에 놓인 스킬 비트마스크(스킬 n = 비트 n−1) */
export function placedMask(slots: ArrayLike<number>, count: number = SKILL_COUNT): number {
  let m = 0;
  for (let i = 0; i < SLOT_COUNT; i++) { const v = slots[i]; if (v >= 1 && v <= count) m |= 1 << (v - 1); }
  return m;
}

/** 배치가 규칙을 지키는지: 길이 12, 각 칸 0..SKILL_COUNT 정수, 같은 스킬은 한 번만 */
export function slotsValid(slots: ArrayLike<number>, count: number = SKILL_COUNT): boolean {
  if (slots.length !== SLOT_COUNT) return false;
  let seen = 0;
  for (let i = 0; i < SLOT_COUNT; i++) {
    const v = slots[i];
    if (!Number.isInteger(v) || v < 0 || v > count) return false;
    if (v > 0) { const b = 1 << v; if (seen & b) return false; seen |= b; }
  }
  return true;
}

/**
 * 자동 배치: 습득했는데 배치에 없는 스킬을 놓는다. "자기 번호 − 1" 칸이 비어 있으면 거기에(기본 배치 = 슬롯 0~4 = 스킬 1~5, 옛 U·I·O·L·P 순서),
 * 아니면 첫 빈 칸(페이지 0 먼저). 이미 놓인 스킬·습득 못 한 스킬은 건드리지 않는다. 바뀌었으면 true.
 */
export function placeLearned(slots: Uint8Array, learnedMask: number, count: number = SKILL_COUNT, extra = 0): boolean {
  let changed = false;
  for (let id = 1; id <= count; id++) {
    if ((learnedMask & (1 << (id - 1))) === 0 || slotOfSkill(slots, id) >= 0) continue;
    // 자기 번호 칸은 기본 12칸 안일 때만(스킬 13의 "번호 − 1" = 12는 환생 추가 칸이라 잠금 검사 없이 고르면 안 된다)
    let at = id - 1 < SLOT_COUNT && slots[id - 1] === 0 ? id - 1 : -1;
    if (at < 0) for (let i = 0; i < SLOT_COUNT; i++) if (slots[i] === 0) { at = i; break; }
    // 기본 12칸이 차면 환생 추가 칸(열려 있을 때만)
    if (at < 0 && extra > 0) for (let i = EXTRA_SLOT_BASE; i < SLOT_TOTAL && i < slots.length; i++) if (slots[i] === 0) { at = i; break; }
    if (at < 0) continue;
    slots[at] = id;
    changed = true;
  }
  return changed;
}

/**
 * 슬롯 배치 바꾸기: skill을 slot에 둔다(skill 0 = 비우기). 이미 다른 칸에 있으면 자리를 맞바꾼다(그 칸 옛 주인이 비운 칸으로 간다).
 * 습득하지 못한 스킬은 둘 수 없다. 거절이면 배치를 바꾸지 않는다.
 */
export function assignSlot(slots: Uint8Array, slot: number, skill: number, learnedMask: number, count: number = SKILL_COUNT, extra = 0): SlotCode {
  if (!Number.isInteger(slot) || slot < 0 || (slot >= SLOT_COUNT && !(extra > 0 && isExtraSlot(slot) && slot < slots.length))) return 'bad_slot';
  if (!Number.isInteger(skill) || skill < 0 || skill > count) return 'bad_skill';
  if (skill === 0) { if (slots[slot] === 0) return 'noop'; slots[slot] = 0; return 'ok'; }
  if ((learnedMask & (1 << (skill - 1))) === 0) return 'not_learned';
  if (slots[slot] === skill) return 'noop';
  const from = slotOfSkill(slots, skill);
  const occupant = slots[slot];
  slots[slot] = skill;
  if (from >= 0) slots[from] = occupant;
  return 'ok';
}

export const SLOTS_TEXT: Readonly<Record<SlotCode, string>> = {
  ok: '슬롯을 바꿨어요.',
  noop: '이미 그 자리예요.',
  bad_slot: '알 수 없는 슬롯이에요.',
  bad_skill: '알 수 없는 스킬이에요.',
  not_learned: '아직 배우지 못한 스킬이에요.',
  not_village: '슬롯은 마을에서만 바꿀 수 있어요.',
};

/** 메모리 상태: 현재 페이지·배치·revision. 바뀔 때 changed()를 부른다(저장 구현이 덮어쓴다) */
export class SlotState implements SlotsView {
  /** 이 배치가 받는 스킬 번호 상한(카엘 5, 라이라 11). c022 */
  readonly count: number;
  constructor(count: number = SKILL_COUNT) { this.count = count; }
  page = 0;
  /** 기본 12칸 + 환생 추가 칸 2(페이지마다 하나, extra가 0이면 늘 비어 있다) */
  readonly all = new Uint8Array(SLOT_TOTAL);
  /** 기본 12칸(all의 앞부분 보기, 옛 형식 그대로) */
  readonly slots = this.all.subarray(0, SLOT_COUNT);
  revision = 0;
  /** 페이지마다 열린 추가 칸 수(0 또는 1, 환생 회차가 정한다) */
  extra = 0;
  private learnedMaskSeen = -1;
  /** 이미 자동 배치 대상으로 본 스킬(저장에 실려 새로고침을 넘는다). 플레이어가 일부러 비운 칸을 다시 채우지 않으려고 "새로 습득한 것"만 놓는다 */
  private seenSkills = 0;

  /** 저장용: 지금까지 본 스킬 비트마스크 */
  get seenMask(): number { return this.seenSkills; }

  /** 새로 습득한 스킬(이전에 못 본 비트)만 자동 배치한다. 같은 마스크로 다시 부르면 아무것도 하지 않는다 */
  place(learnedMask: number): void {
    if (learnedMask === this.learnedMaskSeen) return;
    this.learnedMaskSeen = learnedMask;
    const fresh = learnedMask & ~this.seenSkills;
    this.seenSkills |= learnedMask;
    if (fresh !== 0 && placeLearned(this.all, fresh, this.count, this.extra)) { this.revision++; this.changed(); }
  }

  /**
   * 환생 추가 칸 열기·닫기(레인 R). 닫히면 추가 칸에 있던 스킬을 기본 칸의 빈 자리로 옮긴다(자리가 없으면 비운다 — 그 스킬은 서랍에서 다시 놓는다).
   * 바뀌었으면 저장 대상
   */
  setExtra(n: number): void {
    const v = n > 0 ? 1 : 0;
    // 닫힌 채 추가 칸에 스킬이 남아 있으면(x7을 읽었는데 회차가 0) 닫기를 다시 한다
    if (v === this.extra && (v === 1 || (this.all[EXTRA_SLOT_BASE] === 0 && this.all[EXTRA_SLOT_BASE + 1] === 0))) return;
    this.extra = v;
    if (v === 0) {
      for (let i = EXTRA_SLOT_BASE; i < SLOT_TOTAL; i++) {
        const id = this.all[i];
        if (id === 0) continue;
        this.all[i] = 0;
        for (let k = 0; k < SLOT_COUNT; k++) if (this.slots[k] === 0) { this.slots[k] = id; break; }
      }
    }
    this.revision++;
    this.changed();
  }

  setPage(page: number): void {
    if (!Number.isInteger(page) || page < 0 || page >= SLOT_PAGES || page === this.page) return;
    this.page = page;
    this.revision++;
    this.changed();
  }
  togglePage(): void { this.setPage((this.page + 1) % SLOT_PAGES); }

  /** 습득 마스크는 호출자가 준다(최신 레벨·랭크 기준) */
  assign(slot: number, skill: number, learnedMask: number): SlotCode {
    const code = assignSlot(this.all, slot, skill, learnedMask, this.count, this.extra);
    if (code === 'ok') { this.revision++; this.changed(); }
    return code;
  }

  btnAt(slot: number): number { return slotBtn(this.all, slot); }

  /** 로드한 배치를 넣는다(저장을 부르지 않는다). seen이 없는 옛 저장은 놓인 스킬을 본 것으로 친다(놓이지 않은 습득 스킬은 첫 sync에 배치) */
  load(page: number, slots: ArrayLike<number>, seen?: number, x7?: ArrayLike<number>, xs?: ArrayLike<ArrayLike<number>>): void {
    this.page = page;
    for (let i = 0; i < SLOT_COUNT; i++) this.slots[i] = slots[i];
    // 추가 칸(x7): 기본 칸과 겹치는 스킬은 버린다(옛 빌드가 그 스킬을 다른 칸에 놓은 뒤 돌아온 경우)
    for (let p = 0; p < SLOT_PAGES; p++) {
      const id = x7 ? x7[p] | 0 : 0;
      this.all[EXTRA_SLOT_BASE + p] = id >= 1 && id <= this.count && slotOfSkill(this.slots, id) < 0 && (p === 0 || this.all[EXTRA_SLOT_BASE] !== id) ? id : 0;
    }
    // 옛 빌드 상한 위 스킬(xs [칸, 스킬] 쌍): 비어 있고 겹치지 않는 칸에만 놓는다
    if (xs) for (let k = 0; k < xs.length; k++) {
      const slot = xs[k][0] | 0, id = xs[k][1] | 0;
      if (slot >= 0 && slot < SLOT_TOTAL && id >= 1 && id <= this.count && this.all[slot] === 0 && slotOfSkill(this.all, id) < 0) this.all[slot] = id;
    }
    this.learnedMaskSeen = -1;
    this.seenSkills = seen === undefined ? placedMask(this.slots, this.count) : seen;
    this.revision++;
  }

  protected changed(): void { /* 저장 구현이 덮어쓴다 */ }
}
